import React from 'react';
import { shape, arrayOf, array, bool, string } from 'prop-types';
import styled from 'styled-components';
import { breakpoints } from 'TMConstants';
import faqProps from 'TMPropTypes/faqProps';

import FlexWrapper from 'TMComponents/FlexWrapper';
import HeadTags from 'TMComponents/HeadTags';
import Container from 'TMComponents/Container';
import ContentParser from 'TMComponents/ContentParser';
import { spacing } from '../../designSystem/spacing';
import Question from './Question';
import Accordion from './Accordion';

const ContainerWrapper = styled(Container)``;

const StyledContainer = styled(Container)`
    padding-top: ${spacing.large};
    padding-bottom: ${spacing.huge};
`;

const FAQCard = styled.div`
    background: ${({ theme }) => theme.colorSystem.primary[3]};
    border-radius: ${spacing.large};
    box-shadow: ${({ theme }) => theme.colorSystem.shadows[10]};
    border-top: 2px solid ${({ theme }) => theme.colorSystem.UIElements[4]};
    padding: ${spacing.large};
    max-width: 800px;
    margin: 0 auto;
    @media (max-width: ${breakpoints.mobileTablet}px) {
        max-width: calc(100% - 60px);
        padding: ${spacing.mediumLarge};
    }
`;

const Header = styled(ContentParser)`
    margin-top: ${spacing.mediumLarge};
    margin-bottom: ${spacing.mediumLarge};
    text-align: center;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        font-size: 32px;
        font-weight: 700;
        line-height: 40px;
        color: ${({ theme }) => theme.colorSystem.primary[1]};
        margin: 0;
    }

    h1:after,
    h2:after,
    h3:after,
    h4:after,
    h5:after,
    h6:after {
        content: '.';
        color: ${({ theme }) => theme.colorSystem.secondary[3]};
    }
`;

const Questions = styled(FlexWrapper)`
    div {
        margin-bottom: 48px;
    }
    div:last-child {
        margin-bottom: 0;
    }
`;

const AccordionWrap = styled(FlexWrapper)`
    flex-direction: column;
    gap: ${spacing.medium};
    margin-bottom: ${spacing.medium};
`;

export const getSchema = (questions) => {
    const questionSchema = questions.map(({ title, summary }) => ({
        '@type': 'Question',
        name: title,
        acceptedAnswer: {
            '@type': 'Answer',
            text: `${summary}`
        }
    }));
    return `{"@context":"https://schema.org","@type":"FAQPage","mainEntity":${JSON.stringify(
        questionSchema
    )}}`;
};

const flatStyle = (questions) => (
    <Questions wrap>
        {questions.map(({ id, ...rest }) => (
            <Question key={id} {...rest} />
        ))}
    </Questions>
);

const accordionStyle = (questions) => (
    <AccordionWrap wrap>
        {questions.map(({ id, ...rest }) => (
            <Accordion key={id} {...rest} />
        ))}
    </AccordionWrap>
);

const FAQ = ({ header, accordion, questions, backgroundVariant }) => (
    <ContainerWrapper>
        <StyledContainer>
            <HeadTags>
                <script type="application/ld+json">
                    {getSchema(questions)}
                </script>
            </HeadTags>

            <FAQCard>
                <Header
                    copy={header}
                    backgroundVariant={backgroundVariant}
                />
                {accordion
                    ? accordionStyle(questions)
                    : flatStyle(questions)}
            </FAQCard>
        </StyledContainer>
    </ContainerWrapper>
);

FAQ.propTypes = {
    accordion: bool,
    // eslint-disable-next-line react/forbid-prop-types
    header: array,
    backgroundVariant: string,
    questions: arrayOf(shape(faqProps)).isRequired
};

FAQ.defaultProps = {
    accordion: false,
    backgroundVariant: '#fff'
};

export default FAQ;
